import { AddressLink } from 'Modules'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { AppAdmin } from 'api/app-admin'
import { mapRentalPeriodsFromApi } from 'extends/pto.extend'
import { logError } from 'utils/logger'

interface PTOV2ContextType {
  code?: string | null
  loading?: boolean
  ptoDetail?: any
  contractAddress?: AddressLink | null
  getPTODetail: () => void
}

const PTOV2Context = createContext<PTOV2ContextType>({
  code: null,
  loading: false,
  ptoDetail: null,
  contractAddress: null,
  getPTODetail: () => {},
})

interface PTOV2ContextProps {
  children: ReactNode
}

/**
 * PTO v2 detail context - fetches /admin/pto/v2/:id. Collection stats are read
 * from PTODistribution (use-pool-info) instead of the v1 collection contract.
 */
const PTOV2Provider: React.FC<PTOV2ContextProps> = ({ children }) => {
  const locationParam = useParams()

  const code = locationParam.code || ''

  const [loading] = useState(false)

  const [ptoDetail, setPTODetail] = useState<any>(null)

  const [contractAddress, setContractAddress] = useState<AddressLink | null>(null)

  const getPTODetail = useCallback(async () => {
    try {
      const detailResponse = await AppAdmin.getPTOV2Detail(code)
      if (detailResponse.data.success) {
        const details = {
          ...detailResponse.data.data,
          goldenTickets: (detailResponse.data.data.goldenTickets || []).map((t: any) => ({
            ...t,
            inRewardPool: undefined,
          })),
          // API returns rewardsPerNFT in token base units - expose it in human units
          revenueSharingRental: mapRentalPeriodsFromApi(
            detailResponse.data.data.revenueSharingRental
          ),
        }
        setPTODetail(details)
        setContractAddress(details?.contractAddress)
      }
    } catch (error) {
      setPTODetail(null)
      setContractAddress(null)
      logError('Error fetching details: ', error)
    }
  }, [code])

  useEffect(() => {
    getPTODetail()
  }, [code, getPTODetail])

  const value = {
    code,
    loading,
    ptoDetail,
    contractAddress,
    getPTODetail,
  }

  return <PTOV2Context.Provider value={value}>{children}</PTOV2Context.Provider>
}

const usePTOV2Context = (): PTOV2ContextType => {
  const context = useContext(PTOV2Context)

  if (context === undefined) {
    throw new Error('Context not available')
  }

  return context
}

export { PTOV2Provider, usePTOV2Context }
